import { FileUploadZone } from './FileUploadZone'
import type { AchievementState, FamilyState, MedicalState } from './types'

/**
 * The three question-and-answer sections at the bottom of the Profile Form:
 * Achievement & Skill, Family and Relative, and Medical History.
 *
 * All three share one shape — a No/Yes radio pair that reveals a description
 * box — so the radio pair is a single component here instead of the three
 * hand-written copies the partials carried. Choosing No clears the description,
 * which is what SaveAll did before sending it as an empty string.
 *
 * `value === null` means unanswered and leaves BOTH radios clear. The Razor
 * pre-checked No, which meant the completeness tracker had to either count a
 * section nobody had read or make the candidate toggle Yes and back again to
 * register the answer they already wanted. Unanswered-by-default fixes that:
 * one click completes the section. An unanswered value still submits as false,
 * the same value the pre-checked radio sent.
 */
function YesNo({
  name,
  question,
  value,
  onChange,
}: {
  name: string
  question: string
  value: boolean | null
  onChange: (value: boolean) => void
}): JSX.Element {
  return (
    <div className="mb-3">
      <label className="form-label">{question}</label>
      <div className="mb-3">
        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="radio"
            name={name}
            value="false"
            checked={value === false}
            onChange={() => onChange(false)}
          />
          <label className="form-check-label"> No </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="radio"
            name={name}
            value="true"
            checked={value === true}
            onChange={() => onChange(true)}
          />
          <label className="form-check-label"> Yes </label>
        </div>
      </div>
    </div>
  )
}

/** Port of _Achievement.cshtml. Soft skill and technical skill are required. */
export function AchievementSection({
  value,
  onChange,
  certificate,
  onCertificateChange,
  existingCertificate,
  showErrors,
}: {
  value: AchievementState
  onChange: (patch: Partial<AchievementState>) => void
  certificate: File | null
  onCertificateChange: (file: File | null) => void
  existingCertificate?: string | null
  showErrors: boolean
}): JSX.Element {
  const invalid = (condition: boolean): string => (showErrors && condition ? ' is-invalid' : '')

  return (
    <div className="container mt-4">
      <div className="form-section">
        <form className="needs-validation" id="addAchievementInfo" noValidate>
          <YesNo
            name="IsHaveAchievement"
            question="Do you have the biggest achievement"
            value={value.IsHaveAchievement}
            onChange={(next) =>
              onChange({
                IsHaveAchievement: next,
                AchievementDescription: next ? value.AchievementDescription : '',
              })
            }
          />

          {value.IsHaveAchievement && (
            <div className="mb-3">
              <label className="form-label">Please tell us your achievement description</label>
              <input
                type="text"
                className="form-control"
                id="txtAchievementDescription"
                placeholder="Enter your biggest achievement"
                value={value.AchievementDescription}
                onChange={(e) => onChange({ AchievementDescription: e.target.value })}
              />
            </div>
          )}

          <div className="mb-3">
            <label className="form-label">
              Describe your soft skill <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-control${invalid(!value.SoftSkillDescription.trim())}`}
              id="txtSoftSkill"
              placeholder="Describe your soft skill"
              required
              value={value.SoftSkillDescription}
              onChange={(e) => onChange({ SoftSkillDescription: e.target.value })}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              Technical Skill <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-control${invalid(!value.TechnicalSkill.trim())}`}
              id="txtTechnicalSkill"
              placeholder="Describe your technical skill"
              required
              value={value.TechnicalSkill}
              onChange={(e) => onChange({ TechnicalSkill: e.target.value })}
            />
          </div>

          <div className="mb-3" id="CerificationDesc">
            <FileUploadZone
              id="certificatePath"
              label="If you've got any certifications, feel free to upload them here."
              hint="Upload Certificate"
              fileInfo="JPG, JPEG, PNG up to 2MB"
              icon="fas fa-certificate"
              accept="image/*"
              maxBytes={2 * 1024 * 1024}
              file={certificate}
              onFileChange={onCertificateChange}
              existingName={existingCertificate ?? null}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

/** Port of _Family.cshtml. */
export function FamilySection({
  value,
  onChange,
}: {
  value: FamilyState
  onChange: (patch: Partial<FamilyState>) => void
}): JSX.Element {
  return (
    <form className="needs-validation" id="addFamilyTelcoInfo" noValidate>
      <YesNo
        name="IsHaveFamilyInTBG"
        question="Do you have a family member, partner, or relative who is working for TBIG ?"
        value={value.IsHaveFamilyInTBG}
        onChange={(next) =>
          onChange({ IsHaveFamilyInTBG: next, FamilyInTBG: next ? value.FamilyInTBG : '' })
        }
      />
      {value.IsHaveFamilyInTBG === true && (
        <div className="mb-3" id="HaveFamilyInTBG">
          <label className="form-label">The relationship with you *</label>
          <input
            type="text"
            className="form-control"
            id="txtFamilyInTBG"
            placeholder="Enter their relationship to you"
            value={value.FamilyInTBG}
            onChange={(e) => onChange({ FamilyInTBG: e.target.value })}
          />
        </div>
      )}

      <YesNo
        name="IsHaveFamilyInTelcoIndustry"
        question="Do you have a family member or relative who is working in the telecommunication industry ?"
        value={value.IsHaveFamilyInTelcoIndustry}
        onChange={(next) =>
          onChange({
            IsHaveFamilyInTelcoIndustry: next,
            FamilyInTelcoIndustry: next ? value.FamilyInTelcoIndustry : '',
          })
        }
      />
      {value.IsHaveFamilyInTelcoIndustry === true && (
        <div className="mb-3" id="HaveFamilyInTelcoIndustry">
          <label className="form-label">The relationship with you</label>
          <input
            type="text"
            className="form-control"
            id="txtFamilyInTelcoIndustry"
            placeholder="Enter their relationship to you"
            value={value.FamilyInTelcoIndustry}
            onChange={(e) => onChange({ FamilyInTelcoIndustry: e.target.value })}
          />
        </div>
      )}
    </form>
  )
}

/** Port of _MedicalHistory.cshtml. */
export function MedicalHistorySection({
  value,
  onChange,
}: {
  value: MedicalState
  onChange: (patch: Partial<MedicalState>) => void
}): JSX.Element {
  return (
    <form className="needs-validation" id="addMedicalHistoryInfo" noValidate>
      <YesNo
        name="IsHaveMedicalHistory"
        question="Have you ever experienced any of the following conditions: injuries, chronic illnesses, or infections? For example: tuberculosis, hepatitis, diabetes, asthma, chemical sensitivities, or repetitive strain injuries."
        value={value.IsHaveMedicalHistory}
        onChange={(next) =>
          onChange({
            IsHaveMedicalHistory: next,
            MedicalHistory: next ? value.MedicalHistory : '',
          })
        }
      />
      {value.IsHaveMedicalHistory === true && (
        <div className="mb-3" id="MedicalhistoryDesc">
          <label className="form-label">
            Medical History Description <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="txtMedicalHistoryDesc"
            placeholder="Tells us about your medical history"
            required
            value={value.MedicalHistory}
            onChange={(e) => onChange({ MedicalHistory: e.target.value })}
          />
        </div>
      )}
    </form>
  )
}
